import { useAppSelector } from 'app/hooks';
import Modal from 'components/common/modal';
import usePtacDesignSystemsSummary from 'hooks/use-ptac-design-systems-summary';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { abbreviateNumber } from 'utils/helpers';
import {
  PTAC_SYSTEMS_NOT_READY_TOOLTIP,
  ptacCatalogOptions
} from 'utils/ptac-systems-summary';

type PtacSystemsListModalProps = Readonly<{
  openState: [boolean, Dispatch<SetStateAction<boolean>>];
}>;

const HEADERS = [
  'System',
  'Units',
  'Capacity',
  'Efficiency',
  'Price',
  'In stock'
] as const;

export default function PtacSystemsListModal({
  openState
}: PtacSystemsListModalProps) {
  const { ready, isLoading } = usePtacDesignSystemsSummary();
  const options = useAppSelector(
    (state) => state.ptacSelection.systems.options
  );
  const unitPicksByOptionId = useAppSelector(
    (state) => state.ptacSelection.systems.unitPicksByOptionId
  );
  const rows = useMemo(
    () => (ready ? ptacCatalogOptions(options) : []),
    [ready, options]
  );

  let emptyMessage: string | null = null;
  if (!ready) {
    emptyMessage = PTAC_SYSTEMS_NOT_READY_TOOLTIP;
  } else if (rows.length === 0) {
    emptyMessage = isLoading
      ? 'Loading systems...'
      : 'No systems match this design.';
  }

  return (
    <Modal openState={openState} title='Valid PTAC Systems' size='xl' infoMode>
      {emptyMessage ? (
        <div className='flex items-center justify-center py-8 text-sm text-gray-500'>
          {emptyMessage}
        </div>
      ) : (
        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200 text-sm'>
            <thead className='bg-gray-50'>
              <tr>
                {HEADERS.map((header) => (
                  <th
                    key={header}
                    scope='col'
                    className='px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500'
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-100 bg-white'>
              {rows.map((option) => (
                <tr key={option.uniqueId}>
                  <td className='px-3 py-2 font-medium text-gray-900'>
                    {option.name}
                  </td>
                  <td className='px-3 py-2 tabular-nums text-gray-700'>
                    {unitPicksByOptionId[option.uniqueId]?.length ??
                      option.numberOfZones}
                  </td>
                  <td className='px-3 py-2 tabular-nums text-gray-700'>
                    {abbreviateNumber(option.btu)} BTU
                  </td>
                  <td className='px-3 py-2 tabular-nums text-gray-700'>
                    {Math.round(option.averageSeer)} EER
                  </td>
                  <td className='px-3 py-2 tabular-nums text-gray-700'>
                    ${abbreviateNumber(option.price)}
                  </td>
                  <td className='px-3 py-2 text-gray-700'>
                    {option.inStock ? 'Yes' : 'No'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
